// Schedules a Visual Query Editor surface's schema pulls so it follows the
// engine's schema cache as it streams in.
//
// The VQE pull resolves once tables + columns are in (`livePullSchemaSnapshot`
// — the `…WithColumns` variants); FK edges, CHECK clauses, indexes, routines
// and sequences keep landing afterwards, each announced on the schema bus.
// Pulling on every bus event is not enough once the pull stops waiting for all
// of phase 2:
//  - an event that arrives while a pull is in flight joins that pull (the
//    resolver service de-duplicates same-database pulls), and that pull may
//    have projected the snapshot before the event's data merged — the last
//    artefact of phase 2 would never be picked up. A request during a pull
//    therefore queues exactly one follow-up pull.
//  - phase 2 publishes as often as every 150 ms (REPUBLISH_MIN_INTERVAL_MS);
//    re-projecting a large schema that often would keep the renderer busy.
//    Follow-ups start at most every `minIntervalMs`, trailing, so the last
//    request always gets its pull.

/** Minimum gap between the starts of two pulls (the first pull is immediate). */
export const SCHEMA_PULL_MIN_INTERVAL_MS = 500;

export type SchemaPullScheduler = {
  /** Ask for a pull: now when idle, else once the current one settles (spaced by `minIntervalMs`). */
  readonly request: () => void;
  /** Stop: no further pulls start and `onSettled` is not called again. */
  readonly cancel: () => void;
};

export function createSchemaPullScheduler(
  pull: () => Promise<unknown>,
  onSettled: () => void,
  minIntervalMs: number = SCHEMA_PULL_MIN_INTERVAL_MS,
): SchemaPullScheduler {
  let running = false;
  let queued = false;
  let cancelled = false;
  let lastStart = Number.NEGATIVE_INFINITY;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const start = (): void => {
    timer = undefined;
    if (cancelled) return;
    running = true;
    queued = false;
    lastStart = Date.now();
    const settle = (): void => {
      running = false;
      if (cancelled) return;
      onSettled();
      if (queued) schedule();
    };
    pull().then(settle, settle);
  };

  const schedule = (): void => {
    if (cancelled || running || timer !== undefined) return;
    const wait = lastStart + minIntervalMs - Date.now();
    if (wait <= 0) start();
    else timer = setTimeout(start, wait);
  };

  return {
    request: () => {
      if (cancelled) return;
      queued = true;
      schedule();
    },
    cancel: () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
    },
  };
}
